import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as storage from "../../helper/storage";
import { AppThunk } from "../../store";
import { PartialServiceCases } from "./partialServiceConditions";
import { Models } from "@visonum/network-quality-sdk";
import { getSdk } from "../../networkQuality";
import { getPartialServiceState, initialState, PartialServiceDialogKind, PartialServiceState } from "./partialServiceUtils";
import { isRunningInBrowser } from "../../helper/ssr";
import { getUrlParamValue, nullOrValue } from "../../helper/utils";
import * as R from "ramda";
import { getDays, UpdateAfterMeasurementIn, updateAfterMeasurementPure } from "./updateAfterMeasurementPure";
import { AppAssertionError } from "../errors/errorSlice";
import { MeasurementState, Phase, selectPrepareResult } from "../measurement/measurementSlice";
import { logger } from "../../helper/logger";
import { lastValueFrom } from "rxjs";
import sentryCaptureError from "../sentryReports/sentryCaptureError";
import { matomoPushEvent } from "../matomo/matomoEvents";
import { getNqError } from "../errors/errorUtils";

export const initPartialService = (): AppThunk => (dispatch, _) => {
    window.LinkInterceptor = {
        onCallbackLink() {
            logger.info("onCallbackLink");
            dispatch(matomoPushEvent({
                category: "Partial Service",
                action: "Callback Click",
            }));
            getSdk().updateValue(Models.ValueKey.CallbacksCounter, Models.ValueCommand.Decr).subscribe({
                complete: () => logger.info("Callback counter decremented"),
                error: error => logger.error("Callback counter", error),
            });
        },
    }

    const partialServiceCase = storage.getPartialServiceCase() ?? PartialServiceCases.Default;
    dispatch(partialServiceSlice.actions.update(getPartialServiceState(partialServiceCase, false)));
}

const generateInputForCustomPSCase = (psCase: PartialServiceCases): UpdateAfterMeasurementIn => {

    const addDays = (currentDate: Date, days: number): Date => {
        const result = new Date();
        result.setDate(currentDate.getDate() + days);
        return result;
    }

    const currentDate = new Date();
    const ispFootprint = "UM";
    const partialService = { downloadIndex: 0, uploadIndex: 0 };
    const speedtestState = { downloadClient: true, downloadUdp: true, uploadClient: true };
    const lastModemResetDate = storage.getLastModemResetDate();
    const callbackCounter = 3;
    const currentDownloadSpeed = 80e6;
    const bookedDownloadSpeedMax = 100e6;

    const defaultIn = {
        ispFootprint,
        partialService,
        speedtestState,
        currentDate,
        lastModemResetDate,
        callbackCounter,
        currentDownloadSpeed,
        bookedDownloadSpeedMax,
    }

    switch (psCase) {
        case PartialServiceCases.Default: return defaultIn;
        case PartialServiceCases.FirstStartResultIsNotOkInterfaceHasErrors:
            return {
                ...defaultIn,
                partialService: { downloadIndex: 3, uploadIndex: 2 },
                speedtestState: { downloadClient: false, downloadUdp: false, uploadClient: false },
                lastModemResetDate: null,
            }
        case PartialServiceCases.FirstStartResultIsOkInterfaceHasManyErrors:
            return {
                ...defaultIn,
                partialService: { downloadIndex: 12, uploadIndex: 5 },
                speedtestState: { downloadClient: true, downloadUdp: true, uploadClient: true },
                lastModemResetDate: addDays(currentDate, -40),
            }
        case PartialServiceCases.FirstStartCase3:
            return {
                ...defaultIn,
                partialService: { downloadIndex: 13, uploadIndex: 2 },
                speedtestState: { downloadClient: true, downloadUdp: null, uploadClient: false },
                lastModemResetDate: addDays(currentDate, -8),
            }
        case PartialServiceCases.AgainResultIsNotOkInterfaceHasManyErrors:
            return {
                ...defaultIn,
                partialService: { downloadIndex: 13, uploadIndex: 2 },
                speedtestState: { downloadClient: false, downloadUdp: null, uploadClient: false },
                lastModemResetDate: addDays(currentDate, -1),
            }
    }
}

const getInputFromMeasurement = async (measurementState: MeasurementState): Promise<UpdateAfterMeasurementIn> => {
    if (measurementState.kind !== Phase.FinishedWithModem && measurementState.kind !== Phase.FinishedNoModem) {
        throw new AppAssertionError({ actual: measurementState.kind, expected: "FinishedNoModem or FinishedWithModem" });
    }

    const callbackCounter = await (async () => {
        try {
            logger.info("initPartialService: get callbackCounter...");
            const retVal = await lastValueFrom(getSdk().updateValue(Models.ValueKey.CallbacksCounter, Models.ValueCommand.Read));
            logger.info("initPartialService: get callbackCounter: " + retVal);
            return retVal;
        } catch (err) {
            logger.error("initPartialService: get callbackCounter", err);
            sentryCaptureError(getNqError(err));
            return 0;
        }
    })();

    return {
        currentDownloadSpeed: measurementState.download.speed,
        bookedDownloadSpeedMax: nullOrValue(measurementState.prepareResult.init.modem.bookedDownloadSpeedMax),
        ispFootprint: measurementState.prepareResult.init.connection.ispFootprint ?? "",
        partialService: measurementState.prepareResult.init.partialService,
        speedtestState: measurementState.modem.speedtestState,
        currentDate: new Date(),
        lastModemResetDate: storage.getLastModemResetDate(),
        callbackCounter,
    }
}

export const updateAfterMeasurement = (): AppThunk<Promise<PartialServiceState>> => async (dispatch, getState) => {

    const psCaseFromUrl = ((): PartialServiceCases | null => {
        const url = isRunningInBrowser() ? window.location.href : null;
        const param = getUrlParamValue(url, "pscase");

        if (!R.isNil(param)) {
            switch (param) {
                case PartialServiceCases.Default:
                case PartialServiceCases.FirstStartResultIsNotOkInterfaceHasErrors:
                case PartialServiceCases.FirstStartResultIsOkInterfaceHasManyErrors:
                case PartialServiceCases.FirstStartCase3:
                case PartialServiceCases.AgainResultIsNotOkInterfaceHasManyErrors:
                    return param;
            }
        }

        return null;
    })();

    const measurement = getState().measurement;

    const input = psCaseFromUrl === null ?
        await getInputFromMeasurement(measurement) :
        generateInputForCustomPSCase(psCaseFromUrl);

    logger.debug("PS input", input);
    const output = updateAfterMeasurementPure(input);
    logger.debug("PS output", output);

    storage.setPartialServiceCase(output.partialServiceCase);
    dispatch(partialServiceSlice.actions.update(output.partialServiceState));

    if (output.partialServiceState.partialServiceDialogKind === PartialServiceDialogKind.Reset) {
        storage.setModemResetCount((storage.getModemResetCount() ?? 0) + 1);
        storage.setLastModemResetDate(new Date());
    }

    const days = getDays(input.lastModemResetDate, input.currentDate);
    const prepareResult = selectPrepareResult(measurement);

    dispatch(
        matomoPushEvent({
            category: "Partial Service",
            action: "Partial Service Calculated",
            name: {
                ident: prepareResult === null ? "" : prepareResult.init.speedtest.id,
                downIndex: input.partialService.downloadIndex,
                upIndex: input.partialService.uploadIndex,
                days: days === null ? null : (Math.round(days * 10) / 10),
                lastModemResetDate: input.lastModemResetDate,
                dialog: output.partialServiceState.partialServiceDialogKind,
                modemResetTip: output.partialServiceState.modemResetTipStatus,
                modemResetCount: storage.getModemResetCount(),
                callbackTip: output.partialServiceState.callbackTipStatus,
                callbackCounter: input.callbackCounter,
            },
        })
    );

    return output.partialServiceState;
}

export const partialServiceSlice = createSlice({
    name: 'partialService',
    initialState,
    reducers: {
        reset: () => initialState,
        update: (_, action: PayloadAction<PartialServiceState>) => action.payload,
        hidePartialServiceDialog: (state) => {
            state.partialServiceDialogKind = PartialServiceDialogKind.None;
        },
        updatePartialServiceDialog: (state, action: PayloadAction<PartialServiceDialogKind>) => {
            state.partialServiceDialogKind = action.payload;
        }
    }
});

export const { hidePartialServiceDialog } = partialServiceSlice.actions;

export default partialServiceSlice;